class YeadayProductDetails extends HTMLElement {
 connectedCallback() {
  const featuresPanel = this.querySelector('[data-product-features]');
  const detailsPanel = this.querySelector('[data-product-description]');
  const specificationsPanel = this.querySelector('[data-product-specifications]');
  const specifications = specificationsPanel?.querySelector('.rte');
  const features = featuresPanel?.querySelector('.rte');
  const description = detailsPanel?.querySelector('.rte');
  if (!features || !description) return;
  const normalize = text => text.replace(/\s+/g, ' ').trim().toLowerCase().replace(/[.:]+$/, '');
  const titles = new Set(['features', 'key features', 'product features', 'highlights', "why you'll love it", 'why you’ll love it']);
  const specificationTitles = new Set(['specifications', 'product specifications', 'technical specifications', 'specs']);
  let destination = features;
  let known = new Set();
  const meaningful = node => Boolean(node.textContent.trim() || node.matches?.('img,video,iframe,table,svg,audio') || node.querySelector?.('img,video,iframe,table,svg,audio'));
  const append = node => {
   if (node.nodeType === Node.TEXT_NODE) {
    if (node.textContent.trim() && !known.has(normalize(node.textContent))) {
     const paragraph = document.createElement('p');
     paragraph.textContent = node.textContent;
     destination.append(paragraph);
     known.add(normalize(node.textContent));
    }
    return;
   }
   if (node.matches('ul,ol')) {
    const list = node.cloneNode(false);
    for (const item of [...node.children]) {
     const key = normalize(item.textContent);
     if (!known.has(key) || item.querySelector('img,video,iframe,table,svg,audio')) {
      list.append(item);
      known.add(key);
     }
    }
    if (list.children.length) destination.append(list);
   } else if (meaningful(node) && (!known.has(normalize(node.textContent)) || node.querySelector('img,video,iframe,table,svg,audio') || node.matches('img,video,iframe,svg,audio'))) {
    known.add(normalize(node.textContent));
    destination.append(node);
   }
  };
  for (const heading of [...description.querySelectorAll('h2,h3,h4,h5,h6,p,strong,b')]) {
   const title = normalize(heading.textContent);
   if (!description.contains(heading) || (!titles.has(title) && !specificationTitles.has(title))) continue;
   destination = specificationTitles.has(title) ? specifications : features;
   if (!destination) continue;
   known = new Set([...destination.querySelectorAll('li,p,tr')].map(node => normalize(node.textContent)));
   if (heading.matches('strong,b') && heading.parentElement.matches('p')) continue;
   const section = [];
   let next = heading.nextSibling;
   while (next) {
    if (next.nodeType === Node.ELEMENT_NODE && (next.matches('h1,h2,h3,h4,h5,h6') || titles.has(normalize(next.textContent)) || specificationTitles.has(normalize(next.textContent)))) break;
    section.push(next);
    next = next.nextSibling;
   }
   if (!section.some(meaningful)) continue;
   for (const node of section) {
    append(node);
    if (description.contains(node)) node.remove();
   }
   heading.remove();
  }
  featuresPanel.hidden = !meaningful(features);
  if (specificationsPanel) specificationsPanel.hidden = !meaningful(specifications);
  if (!meaningful(description)) {
   const fallback = this.querySelector('[data-product-details-fallback]');
   if (fallback) description.append(fallback.content.cloneNode(true));
  }
  detailsPanel.hidden = false;
 }
}
if (!customElements.get('yeaday-product-details')) customElements.define('yeaday-product-details', YeadayProductDetails);
class YeadayCardGallery extends HTMLElement {
 connectedCallback() { this.refresh(); }
 refresh() {
  this.abort?.abort(); this.abort = new AbortController();
  const rail = this.querySelector('[data-card-images]'); if (!rail) return;
  const dots = [...this.querySelectorAll('.yc-card__dots span')];
  const arrows = [...this.querySelectorAll('[data-card-direction]')];
  const update = () => {
   const index = Math.round(rail.scrollLeft / Math.max(rail.clientWidth, 1));
   dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
   arrows.forEach(button => button.disabled = Number(button.dataset.cardDirection) < 0 ? index === 0 : index >= rail.children.length - 1);
   [...rail.children].forEach((link, i) => link.tabIndex = i === index ? 0 : -1);
  };
  const move = direction => {
   const index = Math.round(rail.scrollLeft / Math.max(rail.clientWidth, 1));
   rail.scrollTo({left: rail.clientWidth * Math.max(0, Math.min(rail.children.length - 1, index + direction)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
  };
  arrows.forEach(button => button.addEventListener('click', () => move(Number(button.dataset.cardDirection)), {signal:this.abort.signal}));
  rail.addEventListener('keydown', event => { if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); } }, {signal:this.abort.signal});
  if (rail.children.length > 1 && this.closest('.yeaday-template-collection,.yeaday-template-search')) {
   let savedIndex = 0;
   const hover = matchMedia('(hover: hover) and (pointer: fine)');
   this.addEventListener('pointerenter', event => {
    if (!hover.matches || event.pointerType === 'touch') return;
    savedIndex = Math.round(rail.scrollLeft / Math.max(rail.clientWidth, 1));
    rail.scrollTo({left:rail.clientWidth,behavior:'instant'}); update();
   }, {signal:this.abort.signal});
   this.addEventListener('pointerleave', () => {
    if (!hover.matches) return;
    rail.scrollTo({left:rail.clientWidth * savedIndex,behavior:'instant'}); update();
   }, {signal:this.abort.signal});
  }
  rail.addEventListener('scroll', update, {passive:true,signal:this.abort.signal}); update();
 }
 disconnectedCallback() { this.abort?.abort(); }
}
if (!customElements.get('yeaday-card-gallery')) customElements.define('yeaday-card-gallery', YeadayCardGallery);
class YeadayProductCard extends HTMLElement {
 connectedCallback() {
  this.abort?.abort(); this.abort = new AbortController();
  this.addEventListener('click', event => {
   const button = event.target.closest('[data-card-colour]'); if (!button) return;
   event.preventDefault(); event.stopPropagation();
   const template = this.querySelector(`template[data-card-variant="${button.dataset.cardColour}"]`); if (!template) return;
   const gallery = this.querySelector('yeaday-card-gallery');
   gallery.replaceChildren(template.content.cloneNode(true)); gallery.refresh();
   this.querySelectorAll('[data-card-colour]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
   this.querySelector('[data-card-colour-label]').textContent = template.dataset.label;
   this.querySelectorAll('[data-card-product-link]').forEach(link => link.href = template.dataset.url);
   const price = this.querySelector('[data-card-price]'); price.replaceChildren();
   const current = document.createElement('strong'); current.textContent = template.dataset.price; price.append(current);
   if (template.dataset.compare) {const compare = document.createElement('s'); compare.textContent = template.dataset.compare; price.append(compare);}
   const cta = this.querySelector('.yc-card__cta'); cta.textContent = template.dataset.available === 'true' ? cta.dataset.availableLabel : cta.dataset.unavailableLabel;
   this.querySelectorAll('.yc-card__badge').forEach(badge => badge.hidden = true);
   if (template.dataset.available !== 'true') { const badge = document.createElement('span'); badge.className = 'yc-card__badge'; badge.textContent = this.dataset.soldOutLabel; this.querySelector('.yc-card__media').append(badge); }
  }, {signal:this.abort.signal});
 }
 disconnectedCallback() {this.abort?.abort();}
}
if (!customElements.get('yeaday-product-card')) customElements.define('yeaday-product-card', YeadayProductCard);
document.addEventListener('click', (event) => {
 const view = event.target.closest('[data-yc-view]');
 if (view) {
  const results = view.closest('results-list'); if (!results) return;
  results.dataset.yeadayView = view.dataset.ycView;
  results.querySelectorAll('[data-yc-view]').forEach(b => b.setAttribute('aria-pressed', String(b === view)));
 }
 const toggle = event.target.closest('[data-yc-filters]');
 if (toggle) {
  const results = toggle.closest('results-list'); if (!results) return;
  const hidden = results.toggleAttribute('data-hide-filters');
  toggle.setAttribute('aria-expanded', String(!hidden));
  toggle.querySelector('[data-filter-label]').textContent = hidden ? (document.documentElement.dataset.ycShowFilters || 'Show filters') : (document.documentElement.dataset.ycHideFilters || 'Hide filters');
 }
});

class YeadayCategoryRail extends HTMLElement {
 connectedCallback(){
  this.abort?.abort();this.abort=new AbortController();const rail=this.querySelector('[data-category-rail]'),prev=this.querySelector('[data-category-prev]'),next=this.querySelector('[data-category-next]');if(!rail)return;
  const update=()=>{prev.hidden=rail.scrollLeft<2;next.hidden=rail.scrollLeft+rail.clientWidth>=rail.scrollWidth-2;};
  for(const [button,direction]of [[prev,-1],[next,1]])button.addEventListener('click',()=>rail.scrollBy({left:direction*rail.clientWidth*.8,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'}),{signal:this.abort.signal});
  rail.addEventListener('scroll',update,{signal:this.abort.signal,passive:true});this.observer=new ResizeObserver(update);this.observer.observe(rail);update();
 }
 disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();}
}
if(!customElements.get('yeaday-category-rail'))customElements.define('yeaday-category-rail',YeadayCategoryRail);

class YeadayProductFAQ extends HTMLElement {
 connectedCallback(){
  for(const list of this.querySelectorAll('dl')){
   const entries=[...list.children];
   if(!entries.length||entries.length%2||entries.some((e,i)=>e.tagName!==(i%2?'DD':'DT')))continue;
   const fragment=document.createDocumentFragment();
   for(let i=0;i<entries.length;i+=2){const details=document.createElement('details'),summary=document.createElement('summary'),icon=document.createElement('span'),answer=document.createElement('div');summary.textContent=entries[i].textContent;icon.textContent='+';icon.setAttribute('aria-hidden','true');summary.append(icon);answer.className='rte';answer.append(...entries[i+1].childNodes);details.append(summary,answer);fragment.append(details);}
   list.replaceWith(fragment);
  }
 }
}
if(!customElements.get('yeaday-product-faq'))customElements.define('yeaday-product-faq',YeadayProductFAQ);

document.addEventListener('click',event=>{const face=event.target.closest('[data-yeaday-face]');if(face)face.setAttribute('aria-pressed',String(face.getAttribute('aria-pressed')!=='true'));});

// Collection drawer changes are staged until Apply; closing discards pending input.
document.addEventListener('click', event => {
 const apply = event.target.closest('[data-yc-apply-filters]');
 const reset = event.target.closest('[data-yc-reset-filters]');
 const target = apply || reset;
 if (!target) return;
 const component = target.closest('dialog')?.querySelector('facets-form-component');
 if (apply) component?.applyPendingFilters();
 else component?.resetPendingFilters();
});
document.addEventListener('theme-drawer:close', event => {
 if (event.target.matches?.('#filters-drawer') && document.body.classList.contains('yeaday-template-collection')) {
  const component = event.target.querySelector('facets-form-component');
  component?.pendingRequest?.abort();
  const form = component?.querySelector('form');
  const applied = new URLSearchParams(location.search);
  for (const input of form?.querySelectorAll('input[name^="filter."],input[name="sort_by"]') || []) {
   if (input.type === 'checkbox') input.checked = applied.getAll(input.name).includes(input.value);
   else if (input.type === 'radio') input.checked = input.value === (applied.get('sort_by') || form.querySelector('[data-default-sort-by]')?.dataset.defaultSortBy);
   else input.value = applied.get(input.name) || '';
   if (input.type === 'radio') {
    const option = input.closest('[role=option]');
    option?.setAttribute('aria-selected', String(input.checked));
    if (option) option.tabIndex = input.checked ? 0 : -1;
    if (input.checked) { const status = input.closest('sorting-filter-component')?.querySelector('.facets__status'); if (status) status.textContent = input.dataset.optionName; }
   }
  }
  for (const list of form?.querySelectorAll('facet-inputs-component') || []) {
   list.closest('details')?.querySelector('facet-status-component')?.updateListSummary?.([...list.querySelectorAll('input:checked')]);
  }
  const button = event.target.querySelector('[data-yc-apply-filters]');
  if (button) { button.disabled = false; button.removeAttribute('aria-busy'); const count = document.querySelector('.products-count-wrapper [role="status"]')?.textContent.trim().match(/[\d,]+/)?.[0]; button.textContent = count ? `${button.dataset.applyLabel} (${count})` : button.dataset.applyLabel; }
 }
}, true);

const normalizePolicyTitle = text => text.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
for (const policy of document.querySelectorAll('.shopify-policy__container')) {
 const title = policy.querySelector('.shopify-policy__title h1');
 const body = policy.querySelector('.shopify-policy__body');
 if (!title || !body) continue;
 const first = [...body.querySelectorAll('h1,h2,h3,p')].find(element => element.textContent.trim());
 let duplicate = first;
 if (first && normalizePolicyTitle(first.textContent) !== normalizePolicyTitle(title.textContent)) {
  const initial = [...first.childNodes].find(node => node.textContent.trim());
  if (initial?.nodeType === Node.TEXT_NODE && normalizePolicyTitle(initial.textContent) === normalizePolicyTitle(title.textContent)) {
   duplicate = document.createElement('span');
   first.insertBefore(duplicate, initial);
   duplicate.append(initial);
  } else {
   duplicate = initial?.nodeType === Node.ELEMENT_NODE && initial.matches('strong,b,span') ? initial : null;
  }
 }
 if (duplicate && normalizePolicyTitle(duplicate.textContent) === normalizePolicyTitle(title.textContent) && !duplicate.querySelector('a,img,video,iframe,input,button')) {
  duplicate.dataset.ycDuplicatePolicyTitle = '';
  duplicate.hidden = true;
  if (duplicate.nextElementSibling?.matches('br')) {
   duplicate.nextElementSibling.dataset.ycDuplicatePolicyTitle = '';
   duplicate.nextElementSibling.hidden = true;
  }
 }
}

if (location.pathname.replace(/\/$/, '') === '/apps/tracker') {
 const main = document.querySelector('#MainContent');
 const addTrackingTitle = () => {
  const button = main?.querySelector('#track123_submit_button');
  if (!button?.textContent.trim()) return false;
  if (!main.querySelector('.yc-tracking-title')) {
   const heading = document.createElement('h1');
   heading.className = 'yc-tracking-title';
   heading.textContent = button.textContent.trim();
   main.prepend(heading);
  }
  return true;
 };
 if (main && !addTrackingTitle()) {
  const observer = new MutationObserver(() => { if (addTrackingTitle()) observer.disconnect(); });
  observer.observe(main, { childList: true, subtree: true });
 }
}

// Keep the PDP rating link below the sticky header, including Judge.me's lazy setup.
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('.yeaday-template-product .yc-product-rating[href="#judgeme_product_reviews"]');
  const reviewWidget = document.querySelector('.yc-product-reviews .jdgm-review-widget');
  if (!link || !reviewWidget) return;
  event.preventDefault();
  event.stopPropagation();
  reviewWidget.scrollIntoView({
    block: 'start',
    behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
  });
}, { capture: true });

// Decorate app-generated review avatars without changing review data or controls.
for (const reviewWidget of document.querySelectorAll('.yc-product-reviews .jdgm-review-widget')) {
  const setReviewInitials = () => {
    for (const review of reviewWidget.querySelectorAll('.jdgm-rev')) {
      const icon = review.querySelector('.jdgm-rev__icon');
      const author = review.querySelector('.jdgm-rev__author')?.textContent.trim();
      if (!icon || !author || icon.querySelector('img, svg') || icon.style.backgroundImage) continue;
      const initial = Array.from(author)[0].toLocaleUpperCase();
      if (icon.dataset.ycReviewInitial !== initial) icon.dataset.ycReviewInitial = initial;
      icon.setAttribute('aria-hidden', 'true');
    }
  };
  setReviewInitials();
  // Judge.me inserts and replaces cards asynchronously after sorting and paging.
  const reviewObserver = new MutationObserver(setReviewInitials);
  reviewObserver.observe(reviewWidget, { childList: true, subtree: true });
}

class YeadayCarouselIndicator extends HTMLElement {
  static observedAttributes = ['data-carousel-ref'];
  #frame = null;
  #scope = null;
  #recommendations = null;
  #scroller = null;
  #scrollController = null;
  #connectionController = null;
  #resizeObserver = null;
  #mutationObserver = null;
  #observed = new Set();
  #mobile = null;

  connectedCallback() {
    this.#cleanup();
    this.#scope = this.parentElement;
    this.#recommendations = this.closest('product-recommendations[data-intent="related"]');
    if (!this.#scope || !this.#recommendations || !this.closest('.yeaday-template-product')) {
      this.hidden = true;
      return;
    }
    this.#connectionController = new AbortController();
    this.#mobile = matchMedia('(max-width: 749px)');
    this.#mobile.addEventListener('change', this.#schedule, { signal: this.#connectionController.signal });
    this.#resizeObserver = new ResizeObserver(this.#schedule);
    this.#mutationObserver = new MutationObserver(this.#schedule);
    this.#mutationObserver.observe(this.#scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['ref', 'hidden', 'reveal', 'disabled', 'mobile-disabled', 'data-carousel-ref', 'dir'],
    });
    this.#mutationObserver.observe(this.#recommendations, {
      attributes: true,
      attributeFilter: ['data-recommendations-performed', 'data-intent'],
    });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  attributeChangedCallback() {
    if (this.#scope) this.#schedule();
  }

  #cleanup() {
    this.#connectionController?.abort();
    this.#scrollController?.abort();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#observed.clear();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#scope = null;
    this.#recommendations = null;
    this.#scroller = null;
    this.#mobile = null;
  }

  #schedule = () => {
    if (this.#frame !== null || !this.isConnected || !this.#scope) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      if (this.isConnected) this.#update();
    });
  };

  #update() {
    const ref = this.dataset.carouselRef;
    const scroller = ['resourceListCarousel', 'resourceListCarouselMobile'].includes(ref)
      ? this.#scope.querySelector(`slideshow-component[ref="${ref}"] > slideshow-container > slideshow-slides[ref="scroller"]`)
      : null;
    const track = this.querySelector('.yc-carousel-indicator__track');
    const thumb = this.querySelector('.yc-carousel-indicator__thumb');
    if (scroller !== this.#scroller) {
      this.#scrollController?.abort();
      this.#scroller = scroller;
      this.#scrollController = new AbortController();
      scroller?.addEventListener('scroll', this.#schedule, { passive: true, signal: this.#scrollController.signal });
    }
    const slides = scroller ? [...scroller.children].filter(slide => slide.matches('slideshow-slide')) : [];
    const targets = new Set([this.#scope, scroller, track, ...slides].filter(Boolean));
    for (const target of this.#observed) {
      if (!targets.has(target)) this.#resizeObserver.unobserve(target);
    }
    for (const target of targets) {
      if (!this.#observed.has(target)) this.#resizeObserver.observe(target);
    }
    this.#observed = targets;
    const carousel = scroller?.parentElement?.parentElement;
    const range = scroller ? Math.max(0, scroller.scrollWidth - scroller.clientWidth) : 0;
    const renderedSlides = slides.filter(slide => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
    const canScroll = Boolean(
      this.#recommendations?.dataset.intent === 'related' &&
      this.#recommendations?.dataset.recommendationsPerformed === 'true' &&
      scroller?.clientWidth > 0 &&
      renderedSlides.length > 1 &&
      carousel?.getAttribute('disabled') !== 'true' &&
      !(this.#mobile?.matches && carousel?.hasAttribute('mobile-disabled')) &&
      range > 1
    );
    const rtl = scroller && getComputedStyle(scroller).direction === 'rtl';
    const distance = scroller ? (rtl ? -scroller.scrollLeft : scroller.scrollLeft) : 0;
    const position = Math.max(0, Math.min(range, distance));
    // Native slide indices can advance even when every card already fits.
    // Keep its handlers and visible controls; reconcile only their disabled state.
    if (this.#recommendations?.dataset.intent === 'related') {
      for (const [ref, disabled] of [['previous', !canScroll || position <= 1], ['next', !canScroll || range - position <= 1]]) {
        const button = carousel?.querySelector(`:scope > slideshow-container > slideshow-arrows > button[ref="${ref}"]`);
        if (button && button.disabled !== disabled) button.disabled = disabled;
      }
    }
    const visible = Boolean(this.#mobile?.matches && canScroll && track && thumb);
    if (this.hidden === visible) this.hidden = !visible;
    if (!visible) return;
    const progress = position / range;
    const travel = Math.max(0, track.getBoundingClientRect().width - thumb.getBoundingClientRect().width);
    const offset = `${(rtl ? -1 : 1) * progress * travel}px`;
    if (thumb.style.getPropertyValue('--yc-carousel-indicator-offset') !== offset) {
      thumb.style.setProperty('--yc-carousel-indicator-offset', offset);
    }
  }
}
if (!customElements.get('yeaday-carousel-indicator')) {
  customElements.define('yeaday-carousel-indicator', YeadayCarouselIndicator);
}
